/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Button Component For Removing Claims From Manual Worklist
 */

import { useMemo } from 'react';
import { Button, App, ButtonProps, Tooltip } from 'antd';
import { DeleteOne } from '@icon-park/react';

import { MODAL_TYPES } from '../modal';
import { defaultTableSelectionMeta } from 'lib/agGrid/AgGridTable';
import useUpdateManualWorklistEntity from 'hooks/claim/mutate/useUpdateManualWorklistEntity';
import { getTotalSelectionCount, prepareManualWorklistModificationCriteria } from 'utils/claims/workLists';
import { WORKLIST_ENTITY_TYPES } from 'app-constants/claims/workLists';

const selectTooltipText = 'Select claims to remove from this worklist';

export interface IProps extends ButtonProps {
	worklistId: string;
	totalCount: number;
	tableSelectionMeta: ITableSelectionMeta;
	claimStateGroup: ClaimRequest.IGroupedClaimWorkState;
	iconOnly?: boolean;
	setTableSelectionMeta: (tableMeta: ITableSelectionMeta) => void;
}

const RemoveClaimFromManualWorklist: React.FC<IProps> = ({
	worklistId,
	totalCount,
	tableSelectionMeta,
	claimStateGroup,
	setTableSelectionMeta,
	iconOnly,
	disabled,
	...props
}) => {
	const { modal } = App.useApp();
	const { mutateAsync } = useUpdateManualWorklistEntity();

	const totalSelectionCount = useMemo(() => getTotalSelectionCount(tableSelectionMeta, totalCount), [tableSelectionMeta, totalCount]);
	const showSelectTooltipText = totalSelectionCount === 0;

	const getLabel = () => {
		if (totalSelectionCount === 0) {
			return 'Remove from Worklist';
		}
		return `Remove from Worklist (${totalSelectionCount})`;
	};

	const removeClaims = () => {
		modal.confirm({
			...MODAL_TYPES.warning,
			content: 'Are you sure you want to remove the selected claims from the worklist?',
			onOk: () => {
				return mutateAsync(
					{
						entityType: WORKLIST_ENTITY_TYPES.claim,
						multipleWorklistPatch: {
							worklistIds: [worklistId],
							removeClaims: {
								conjunction: 'AND',
							},
						},
						claimStateGroup,
						criteria: prepareManualWorklistModificationCriteria(tableSelectionMeta),
					},
					{
						onSuccess: () => setTableSelectionMeta({ ...defaultTableSelectionMeta, criteria: tableSelectionMeta?.criteria }),
					},
				);
			},
		});
	};

	if (iconOnly) {
		return (
			<Tooltip title={showSelectTooltipText ? selectTooltipText : 'Remove from Worklist'}>
				<Button
					color='default'
					danger
					icon={<DeleteOne />}
					onClick={removeClaims}
					disabled={totalSelectionCount === 0 || disabled}
					{...props}
				/>
			</Tooltip>
		);
	}

	return (
		<Tooltip title={showSelectTooltipText ? selectTooltipText : ''}>
			<Button danger onClick={removeClaims} disabled={totalSelectionCount === 0 || disabled} {...props}>
				{getLabel()}
			</Button>
		</Tooltip>
	);
};

export default RemoveClaimFromManualWorklist;
